import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Accordion Component
 *
 * A customizable accordion built using Radix UI primitives.
 * Supports collapsible sections with optional custom icons
 * for open and closed states. Provides smooth animations and
 * accessible markup by leveraging Radix UI's underlying logic.
 */

const Accordion = AccordionPrimitive.Root;

// Wrapper for individual accordion items
const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

// Custom trigger supporting default or user-defined icons
const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    openIcon?: React.ReactNode;
    closeIcon?: React.ReactNode;
  }
>(({ className, children, openIcon, closeIcon, ...props }, ref) => {
  // Detect if user provided custom icons
  const hasCustomIcons = Boolean(openIcon || closeIcon);

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "group flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
          className,
        )}
        {...props}
      >
        {children}
        {hasCustomIcons ? (
          <div className="shrink-0">
            <div className="group-data-[state=open]:hidden">
              {closeIcon || <ChevronDown className="h-4 w-4" />}
            </div>
            <div className="hidden group-data-[state=open]:block">
              {openIcon || <ChevronDown className="h-4 w-4 rotate-180" />}
            </div>
          </div>
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

// Animated content section for accordion panels
const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all"
    {...props}
  >
    <div className={cn("pt-0 pb-4", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
